import React, { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  makeStyles,
  TextField,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  IconButton,
  useMediaQuery,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SuccessDialog from "../../common/SuccessDialog";
import ComingSoonDialog from "../../common/ComingSoonDialog";
import ActionDialog from "../../common/ActionDialog";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import theme from "../../../themes";
import { PhoneNumberFormatCustom } from "../../common/utilities/PhoneNumberFormat";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  LoginInfoEmailTypes,
  useCreateChamberMembersMutation,
  useGetSampleLoginInfoEmailQuery,
  useSendLoginInfoEmailToChamberMemberMutation,
  useSetNewTemporaryChamberMemberPasswordMutation,
  UserType,
} from "../../../graphql/types";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useFormik } from "formik";
import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";
import useRedirect from "../../common/Hooks/useRedirect";
import CreateChamberMemberSuggestedAlternativePlusAddressedEmailUsernameModal from "../../common/ConfirmationModal/CreateChamberMemberSuggestedAlternativePlusAddressedEmailUsernameModal";
import Categories from "../../common/utilities/Categories";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import { CSVLink } from "react-csv";
import _without from "lodash/without";
import LogoPicker from "../../common/utilities/LogoPicker";
import ProvincePicker from "../../common/utilities/ProvincePicker";
import {
  getInitialStateOfCustomContacts,
} from "../../common/utilities/MultiContactForm";
import { removePlusAddress } from "../../common/utilities/removePlusAddress";
import IdaChatbotIcon from "../../../images/ida-chatbot-icon.png";
import AIRewriteIcon from "../../../images/airewrite.png";
import IDAOverwriteDialog from "../../common/IDAOverwriteDialog";
import UploadRoundedIcon from "../../../images/upload-rounded.png";
import MdiEmailFastOutlineIcon from "../../../images/mdi_email-fast-outline.png";
import IDAGenerateModal from "../../common/IDAGenerateModal";
import OkIcon from "../../../images/ok-icon.png";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import { openInstructionPdf } from "../../common/openInstructionPdf";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {
  CHARACTER_LIMIT_DESCRIPTION,
  addressValidationSchema,
  scrollToFirstValidationError,
} from "./memberFormUtils";
import UnsavedChangesPrompt from "../../common/UnsavedChangesPrompt";
import FeaturedMemberBannerGuidance from "../../common/FeaturedMemberBannerGuidance";
const useStyles = makeStyles((themes: any) => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    minHeight: "100vh",
    paddingTop: "10px",
    overflowX: "hidden" as const,
  },
  background: {},

  title: {
    fontSize: "32px",
    color: "white",
    fontWeight: "bold",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      fontSize: "22px",
    },
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "15px 40px 15px",
    [theme.breakpoints.down("sm")]: {
      padding: "12px 16px",
    },
  },
  pageHeaderButtonRow: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > div": { width: "100%" },
      "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
        width: "100%",
        marginTop: 0,
      },
    },
  },
  headerIcons: {
    display: "flex",
    gap: "15px",
    alignItems: "center",
  },
  headerIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  contentContainer: {
    height: "100vh",
    backgroundColor: "#F6F6F6",
  },
  sectionHeader: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "30px",
    padding: "12px 32px",
    display: "inline-block",
    position: "relative" as const,
    top: "-24px",
    left: "0",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 18px",
      top: "-20px",
      maxWidth: "calc(100% - 8px)",
    },
  },
  sectionTitle: {
    color: "#FFFFFF",
    fontSize: "20px",
    fontWeight: 700,
    margin: 0,
    textAlign: "center" as const,
    lineHeight: "24px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "18px",
    },
  },
  sectionHelperText: {
    color: "#000000",
    fontSize: "16px",
    lineHeight: "18px",
    fontWeight: 400,
    marginBottom: "20px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
      marginBottom: "14px",
    },
  },
  formSection: {
    maxWidth: "1096px",
    margin: "50px auto 50px auto",
    padding: "0px 30px 30px 30px",
    border: "1px solid #9E9E9E",
    borderRadius: "16px",
    position: "relative",
    boxSizing: "border-box" as const,
    overflow: "visible",
    backgroundColor: "#FFFFFF",
    [theme.breakpoints.down("sm")]: {
      margin: "40px 12px 24px 12px",
      padding: "0 16px 20px 16px",
      maxWidth: "calc(100% - 24px)",
    },
  },
  formSectionDashed: {
    maxWidth: "1096px",
    margin: "50px auto 50px auto",
    padding: "0px 30px 30px 30px",
    border: "1px dashed #9E9E9E",
    borderRadius: "16px",
    position: "relative",
    boxSizing: "border-box" as const,
    overflow: "visible",
    backgroundColor: "#FFFFFF",
    [theme.breakpoints.down("sm")]: {
      margin: "40px 12px 24px 12px",
      padding: "0 16px 20px 16px",
      maxWidth: "calc(100% - 24px)",
    },
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginBottom: "20px",
    alignItems: "start",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  companyInfoGrid: {
    display: "grid",
    gridTemplateColumns: "409fr 537fr",
    gap: "20px 20px",
    gridTemplateAreas: `
      "companyName description"
      "companyWebsite description"
      "contactName phone"
      "contactEmail googleReview"
      "logo categories"
    `,
    alignItems: "start",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gridTemplateAreas: `
        "companyName"
        "description"
        "companyWebsite"
        "contactName"
        "phone"
        "contactEmail"
        "googleReview"
        "logo"
        "categories"
      `,
    },
  },
  formRowSingle: {
    display: "grid",
    gridTemplateColumns: "1fr",
    marginBottom: "20px",
  },
  orangeHelperText: {
    color: theme.palette.primary.main,
    fontSize: "14px",
    marginBottom: "15px",
    lineHeight: "1.5",
  },
  checkboxLabel: {
    marginBottom: "15px",
    marginLeft: "0",
  },
  boxTitle: {
    fontWeight: "bold",
    fontSize: "4.3vh",
    textAlign: "center",
    marginTop: "11px",
  },
  boxValue: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
    fontSize: "21vh",
    textAlign: "center",
  },
  buttonContainer: {
    display: "flex",
    paddingBottom: "9px",
    paddingLeft: "24px",
  },
  titleContent: {
    display: "flex",
    flexDirection: "column-reverse",
    [theme.breakpoints.up("lg")]: {
      paddingTop: "10px",
    },
    "@media (max-width: 768px)": {
      minHeight: "auto",
    },
  },
  companyContainer: {
    display: "flex",
    justifyContent: "flex-end",
    flexDirection: "row-reverse",
    width: "431px",
    margin: "auto",
    backgroundColor: "white",
    marginBottom: "20px",
    borderRadius: "15px",
    [theme.breakpoints.down("sm")]: {
      width: "231px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
    },
  },
  imageContainer: {
    width: "100px",
    height: "100px",
    borderRadius: "200px",
    backgroundColor: "#F2F2F6",
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      width: "75px",
      height: "75px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "100px",
      height: "100px",
    },
  },
  images: {
    borderRadius: "200px",
    margin: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "65px",
      height: "65px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "90px",
      height: "90px",
    },
  },
  companyTitle: {
    margin: "auto",
    alignItems: "center",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1em",
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "2em",
    },
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  companyInputWithToolTipContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingLeft: "36px",
  },
  companyInput: {
    paddingBottom: "10px",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#FFFFFF",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: theme.palette.text.primary,
        borderRadius: "8px",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.text.primary,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.text.primary,
      },
    },
    "& label.Mui-focused": {
      color: "#000000",
    },
    "& label.Mui": {
      color: "black",
    },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      color: "#333333",
      "&::placeholder": {
        color: "#333333",
        opacity: 1,
      },
    },
  },
  fieldWithIcon: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  fieldLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    fontSize: "18px",
    color: "#000000",
    fontWeight: 600,
    marginBottom: "8px",
  },
  requiredAsterisk: {
    color: "red",
  },
  logoHelperText: {
    fontSize: "16px",
    color: theme.palette.text.primary,
    marginBottom: "12px",
    lineHeight: "18px",
  },
  categoryHelperText: {
    fontSize: "16px",
    color: theme.palette.text.primary,
    marginBottom: "12px",
    lineHeight: "18px",
  },
  idaHelperContainer: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "10px",
    padding: "2px 5px",
    marginTop: "8px",
    backgroundColor: "#FFFFFF",
  },
  idaIcon: {
    width: "33px",
    height: "26px",
    objectFit: "contain" as const,
  },
  idaHelperText: {
    fontWeight: 400,
    fontSize: "12px",
    color: theme.palette.primary.main,
    lineHeight: "15px",
    "& b": {
      fontWeight: 700,
    },
  },
  idaGenerateButton: {
    backgroundColor: "#4CAF50",
    border: "1px solid #4CAF50",
    borderRadius: "10px",
    padding: "1px 13px",
    height: "17px",
    minWidth: "71px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#388E3C",
    },
  },
  idaGenerateButtonText: {
    fontWeight: 400,
    fontSize: "10px",
    color: "#FFFFFF",
    lineHeight: "15px",
  },
  // IDA Generate Dialog styles
  idaDialogPaper: {
    borderRadius: "20px",
    maxWidth: "520px",
    width: "520px",
    overflow: "hidden",
    padding: "0",
    backgroundColor: "#FFFFFF",
    position: "relative" as const,
  },
  idaDialogCloseButton: {
    position: "absolute" as const,
    right: "10px",
    top: "10px",
    backgroundColor: (theme.palette as any).delete.main,
    color: "white",
    width: "28px",
    height: "28px",
    padding: 0,
    zIndex: 1,
    "&:hover": { backgroundColor: "#D41920" },
  },
  idaDialogMascotRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "4px",
    marginBottom: "8px",
    paddingLeft: "20px",
    paddingTop: "16px",
  },
  idaDialogSpeechBubble: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #ccc",
    borderRadius: "12px",
    padding: "6px 12px",
    fontSize: "12px",
    color: "#333",
  },
  idaDialogMascotImg: {
    width: "80px",
    height: "80px",
    objectFit: "contain" as const,
  },
  idaDialogTitle: {
    fontWeight: 700,
    fontSize: "18px",
    color: theme.palette.primary.main,
    textAlign: "center" as const,
    lineHeight: "1.4",
    margin: "0 24px 8px 24px",
  },
  idaDialogSubtitle: {
    fontSize: "13px",
    color: "#333",
    textAlign: "center" as const,
    lineHeight: "1.5",
    margin: "0 24px 12px 24px",
  },
  idaDialogTextarea: {
    margin: "0 24px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "12px",
    fontSize: "13px",
    lineHeight: "1.6",
    resize: "vertical" as const,
    minHeight: "100px",
    outline: "none",
    "&:focus": { borderColor: theme.palette.primary.main },
  },
  idaDialogGenerateBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    borderRadius: "8px",
    padding: "10px 32px",
    textTransform: "uppercase" as const,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    margin: "16px auto 20px auto",
    "&:hover": { backgroundColor: "#D4A353" },
  },
  overwriteDialogPaper: {
    borderRadius: "20px",
    maxWidth: "600px",
    width: "600px",
    position: "relative" as const,
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  overwriteCloseButton: {
    position: "absolute" as const,
    right: "12px",
    top: "12px",
    backgroundColor: (theme.palette as any).delete.main,
    color: "white",
    width: "30px",
    height: "30px",
    padding: 0,
    zIndex: 1,
    "&:hover": { backgroundColor: "#D41920" },
  },
  overwriteIconCircle: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px auto",
  },
  overwriteTitle: {
    fontWeight: 700,
    fontSize: "20px",
    textAlign: "center" as const,
    color: "#000",
    marginBottom: "8px",
  },
  overwriteDesc: {
    fontSize: "14px",
    textAlign: "center" as const,
    color: "#333",
    lineHeight: "1.6",
    marginBottom: "16px",
    padding: "0 20px",
  },
  overwritePreviewBox: {
    border: `1px solid ${theme.palette.success.main}`,
    borderRadius: "6px",
    padding: "12px 6px",
    margin: "0 50px",
    maxHeight: "200px",
    overflow: "auto",
  },
  overwritePreviewLabel: {
    fontSize: "13px",
    fontWeight: 700,
    color: theme.palette.success.main,
    marginBottom: "8px",
  },
  overwritePreviewText: {
    fontSize: "13px",
    color: theme.palette.text.primary,
    lineHeight: "1.6",
  },
  overwriteButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    margin: "20px 0 24px 0",
  },
  overwriteRegenBtn: {
    backgroundColor: theme.palette.success.main,
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    borderRadius: "8px",
    padding: "10px 24px",
    textTransform: "uppercase" as const,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "&:hover": { backgroundColor: "#009A1B" },
  },
  overwriteConfirmBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    borderRadius: "8px",
    padding: "10px 32px",
    textTransform: "uppercase" as const,
    "&:hover": { backgroundColor: "#C99028" },
  },
  idaSuccessDialogPaper: {
    borderRadius: "20px",
    maxWidth: "420px",
    width: "420px",
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
    position: "relative",
  },
  idaSuccessIconCircle: {
    width: "90px",
    height: "90px",
    borderRadius: "50%",
    backgroundColor: "#22C55E",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px auto",
  },
  idaSuccessTitle: {
    fontWeight: 700,
    fontSize: "20px",
    textAlign: "center" as const,
    color: "#000",
    lineHeight: "1.5",
  },
  descriptionHelperText: {
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    color: "#24272C",
    lineHeight: "22px",
    marginBottom: "4px",
  },
  sectionHeaderBlack: {
    backgroundColor: "#000000",
    borderRadius: "30px",
    padding: "12px 32px",
    display: "inline-block",
    position: "relative" as const,
    top: "-24px",
    left: "0",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 18px",
      top: "-20px",
      maxWidth: "calc(100% - 8px)",
    },
  },
  bannerDescText: {
    fontSize: "16px",
    lineHeight: "18px",
    color: theme.palette.text.secondary,
    marginTop: "0px",
    marginBottom: "20px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },
  bannerNotUpgradedText: {
    fontSize: "20px",
    lineHeight: "24px",
    color: theme.palette.text.secondary,
    textAlign: "left" as const,
    marginBottom: "16px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "15px",
      lineHeight: "20px",
    },
  },
  bannerAspectRatioText: {
    fontSize: "20px",
    lineHeight: "24px",
    color: "#000000",
    textAlign: "left" as const,
    marginBottom: "24px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },
  bannerGrid: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: "20px",
    marginBottom: "24px",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "16px 12px",
      alignItems: "stretch",
      justifyContent: "stretch",
      width: "100%",
    },
  },
  bannerSlotLabel: {
    fontSize: "20px",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textAlign: "center" as const,
    lineHeight: "24px",
    marginBottom: "16px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "15px",
      marginBottom: "8px",
    },
  },
  bannerSlot: {
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
    padding: "22px 12px",
    gap: "10px",
    width: "182px",
    height: "114px",
    backgroundColor: "#9E9E9E",
    borderRadius: "10px",
    boxSizing: "border-box" as const,
    cursor: "not-allowed",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "90px",
      padding: "12px 8px",
      gap: "8px",
    },
  },
  bannerSlotIcon: {
    width: "36px",
    height: "36px",
    filter: "brightness(0) invert(1)",
    [theme.breakpoints.down("sm")]: {
      width: "28px",
      height: "28px",
    },
  },
  bannerSlotText: {
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "24px",
    textAlign: "center" as const,
    color: "#FFFFFF",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      lineHeight: "1.3",
    },
  },
  bannerFooterText: {
    fontSize: "16px",
    lineHeight: "24px",
    color: "#000000",
    textAlign: "center" as const,
    marginBottom: "24px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },
  previewBannersButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#808080",
    color: "white",
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "24px",
    padding: "15px 96px",
    borderRadius: "12px",
    textTransform: "none" as const,
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "#808080",
    },
    "&.Mui-disabled": {
      backgroundColor: "#808080",
      color: "#FFFFFF",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      padding: "14px 24px",
      fontSize: "16px",
    },
  },

  content: {
    background: "#FFFFFF",
    flexGrow: 1,
    borderRadius: "16px",
    margin: "10px 20px 20px",
    minHeight: "85vh",
    [theme.breakpoints.down("sm")]: {
      margin: 0,
    },
    flex: "1",
    marginTop: "33px",
    paddingTop: "30px",
    paddingBottom: "33px",
  },
  invalidError: {
    textAlign: "center",
    color: "red",
  },
  companyInputLogo: {
    display: "flex",
    justifyContent: "flex-end",
    overflow: "hidden",
    padding: "16px",
    outline: "none",
    border: "1px dashed #888888",
    borderRadius: "8px",
    width: "100%",
    height: "127px",
    boxSizing: "border-box" as const,
  },
  labelLogo: {
    display: "flex",
    flexDirection: "row-reverse",
    width: "100%",
    justifyContent: "space-between",
    color: "#9A9A9C",
    font: "inherit",
    fontSize: "1.18676em",
  },
  switch: {
    "& .MuiSwitch-colorPrimary.Mui-checked": {
      color: theme.palette.primary.main,
    },
    "& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track": {
      backgroundColor: theme.palette.primary.main,
    },
  },
  selectedValue: {
    "& .MuiSelect-select:focus": {
      background: "transparent",
    },
    "& .MuiSelect-icon": {
      height: "53px",
      backgroundColor: theme.palette.primary.main,
      top: "-16px",
      borderRadius: "0px 7px 7px 0px",
      width: "55px",
    },
    "& .MuiSelect-iconOpen": {
      height: "53px",
      backgroundColor: theme.palette.primary.main,
      top: "-16px",
      borderRadius: "0px 0px 0px 0px",
      width: "55px",
    },
    "& .MuiPaper-root": {
      minWidth: "430px!important",
      top: "269px!important",
      left: "256px!important",
    },
  },
  chamberInput: {
    backgroundColor: "#F2F2F6",
    borderRadius: "14px",
    border: "1px solid #E8E8E8",
    paddingLeft: "10px",
    width: "100%",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#E8E8E8",
        borderRadius: "15px",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& label.Mui-focused": {
      color: "#0000008a",
    },
    "& select": {},
    "& label.Mui": {
      color: "black",
      paddingLeft: "15px",
    },
    "& .MuiPaper-root": {
      minWidth: "430px!important",
      top: "269px!important",
      left: "256px!important",
    },
  },
  switchContainer: {
    display: "flex",
    flexDirection: "row",
    marginTop: "30px",
  },
  switchText: {
    margin: "10px",
  },
  companyDescription: {
    backgroundColor: "#F2F2F6",
    borderRadius: "15px",
    border: "1px solid #E8E8E8",
    paddingLeft: "10px",
    paddingRight: "10px",
    width: "100%",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#E8E8E8",
        borderRadius: "15px",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& label.Mui-focused": {
      color: "#0000008a",
    },
    "& select": {},
    "& label.Mui": {
      color: "black",
      paddingLeft: "10px",
    },
    "& .MuiPaper-root": {
      minWidth: "430px!important",
      top: "269px!important",
      left: "256px!important",
    },
  },
  companyDescriptionInput: {
    "& .MuiSelect-select:focus": {
      background: "transparent",
    },
    "& .MuiSelect-icon": {
      height: "53px",
      backgroundColor: theme.palette.primary.main,
      top: "-16px",
      borderRadius: "0px 7px 7px 0px",
      width: "55px",
    },
    "& .MuiSelect-iconOpen": {
      height: "53px",
      backgroundColor: theme.palette.primary.main,
      top: "-16px",
      borderRadius: "0px 0px 0px 0px",
      width: "55px",
    },
    "& .MuiPaper-root": {
      minWidth: "430px!important",
      top: "269px!important",
      left: "256px!important",
    },
    "& .MuiInputBase-inputMultiline": {
      overflow: "scroll !important",
      height: "79px!important",
    },
  },
  modal: {
    border: "unset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    height: "100%",
    borderRadius: "5px",
    textAlign: "center",
    maxHeight: "800px",
    width: "740px",
    maxWidth: "100%",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    backgroundColor: "#cfd8dc",
    padding: theme.spacing(6),
    position: "relative",
  },
  yesButton: {
    margin: "10px",
    marginTop: "20px",
    width: "230px",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: "bold",
    height: "40px",
  },
  emailDialogPaper: {
    borderRadius: "20px",
    maxWidth: "610px",
    width: "610px",
    position: "relative",
    overflow: "visible",
  },
  dialogCloseButton: {
    position: "absolute",
    right: "15px",
    top: "15px",
    backgroundColor: (theme.palette as any).delete.main,
    color: "white",
    width: "30px",
    height: "30px",
    padding: 0,
    "&:hover": {
      backgroundColor: "#D41920",
    },
  },
  dialogIconContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "0px",
    marginTop: "15px",
  },
  dialogIconError: {
    width: "112px",
    height: "112px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: (theme.palette as any).delete.main,
  },
  dialogTitle: {
    fontSize: "25px",
    fontWeight: 700,
    lineHeight: "200%",
    textAlign: "center" as const,
    color: "#000000",
    margin: "0",
  },
  dialogDescription: {
    fontSize: "16px",
    lineHeight: "200%",
    textAlign: "left" as const,
    color: "#000000",
    marginBottom: "0px",
  },
  dialogButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    marginTop: "24px",
  },
  dialogButtonOk: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "150%",
    textTransform: "uppercase" as const,
    borderRadius: "12px",
    padding: "15px 96px",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    "&:hover": {
      backgroundColor: "#D4A353",
    },
  },
  successDialogPaper: {
    borderRadius: "20px",
    border: "1px solid #888888",
    maxWidth: "800px",
    width: "800px",
    position: "relative",
    overflow: "visible",
    backgroundColor: "#FFFFFF",
  },
  successCloseButton: {
    position: "absolute",
    right: "14px",
    top: "14px",
    backgroundColor: (theme.palette as any).delete.main,
    color: "white",
    width: "30px",
    height: "30px",
    padding: 0,
    borderRadius: "100px",
    zIndex: 1,
    "&:hover": {
      backgroundColor: "#D41920",
    },
  },
  successIconCircle: {
    width: "105px",
    height: "105px",
    borderRadius: "50%",
    backgroundColor: "#00B621",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "14px auto 0 auto",
  },
  successTitle: {
    fontWeight: 700,
    fontSize: "22px",
    lineHeight: "1.4",
    textAlign: "center" as const,
    color: "#000000",
    margin: "10px 0 0 0",
    textTransform: "uppercase" as const,
  },
  successDesc: {
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "150%",
    textAlign: "left" as const,
    color: "#000000",
    margin: "10px auto 0 auto",
    maxWidth: "780px",
    padding: "0 30px",
  },
  successStackedContainer: {
    border: "1px solid #000000",
    borderRadius: "16px",
    marginTop: "20px",
    marginLeft: "30px",
    marginRight: "30px",
    padding: "24px 28px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "20px",
  },
  successSectionTitle: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 700,
    fontSize: "18px",
    color: "#000000",
    marginBottom: "8px",
    textAlign: "left" as const,
  },
  successSectionDesc: {
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "150%",
    color: "#000000",
    margin: "0 0 12px 0",
    textAlign: "left" as const,
  },
  successDownloadButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: "bold",
    fontSize: "12px",
    textTransform: "uppercase" as const,
    padding: "10px 28px",
    borderRadius: "12px",
    boxShadow: "none",
    whiteSpace: "nowrap" as const,
    fontFamily: "DM Sans, sans-serif",
    "&:hover": {
      backgroundColor: "#D4A353",
      boxShadow: "none",
    },
  },
  successButtonsRow: {
    display: "flex",
    flexDirection: "row" as const,
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap" as const,
  },
  successSendEmailButton: {
    backgroundColor: "#00B621",
    color: "#FFFFFF",
    fontWeight: "bold",
    fontSize: "12px",
    textTransform: "uppercase" as const,
    padding: "10px 28px",
    borderRadius: "12px",
    boxShadow: "none",
    whiteSpace: "nowrap" as const,
    fontFamily: "DM Sans, sans-serif",
    "&:hover": {
      backgroundColor: "#009A1C",
      boxShadow: "none",
    },
  },
  successPreviewEmailButton: {
    backgroundColor: "#000000",
    color: "#FFFFFF",
    fontWeight: "bold",
    fontSize: "12px",
    textTransform: "uppercase" as const,
    padding: "10px 28px",
    borderRadius: "12px",
    boxShadow: "none",
    whiteSpace: "nowrap" as const,
    fontFamily: "DM Sans, sans-serif",
    "&:hover": {
      backgroundColor: "#333333",
      boxShadow: "none",
    },
  },
  // Credentials Downloaded sub-modal
  credentialsDialogPaper: {
    borderRadius: "20px",
    maxWidth: "505px",
    width: "505px",
    position: "relative",
    overflow: "visible",
  },
  credentialsCloseButton: {
    position: "absolute",
    right: "10px",
    top: "10px",
    backgroundColor: (theme.palette as any).delete.main,
    color: "white",
    width: "30px",
    height: "30px",
    padding: 0,
    borderRadius: "100px",
    "&:hover": {
      backgroundColor: "#D41920",
    },
  },
  credentialsIconCircle: {
    width: "105px",
    height: "105px",
    borderRadius: "50%",
    backgroundColor: theme.palette.success.main,
    border: "1px solid #FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "10px auto 0 auto",
  },
  credentialsTitle: {
    fontWeight: 600,
    fontSize: "25px",
    lineHeight: "200%",
    textAlign: "center" as const,
    color: "#000000",
    margin: "0",
  },
  credentialsDesc: {
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "200%",
    textAlign: "center" as const,
    color: "#000000",
    margin: "0 auto",
    maxWidth: "359px",
  },
  credentialsDownloadBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "150%",
    textTransform: "uppercase" as const,
    padding: "15px 96px",
    borderRadius: "12px",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    "&:hover": {
      backgroundColor: "#D4A353",
    },
  },
  // Email Sent Successfully sub-modal
  emailSentDialogPaper: {
    borderRadius: "20px",
    maxWidth: "505px",
    width: "505px",
    position: "relative",
    overflow: "visible",
  },
  emailSentIconCircle: {
    width: "105px",
    height: "105px",
    borderRadius: "50%",
    backgroundColor: theme.palette.success.main,
    border: "1px solid #FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "25px auto 0 auto",
  },
  emailSentTitle: {
    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "200%",
    textAlign: "center" as const,
    color: "#000000",
    margin: "0",
  },
  // Preview Email sub-modal
  previewEmailDialogPaper: {
    borderRadius: "20px",
    maxWidth: "966px",
    width: "966px",
    backgroundColor: "#E6E6E6",
    position: "relative",
    overflow: "visible",
  },
  previewEmailHeader: {
    display: "flex",
    alignItems: "center",
    padding: "27px 30px",
    position: "relative",
  },
  previewEmailBackButton: {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "38px",
    height: "38px",
    padding: 0,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  previewEmailTitle: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 700,
    fontSize: "25px",
    lineHeight: "200%",
    textAlign: "center" as const,
    color: "#000000",
    flex: 1,
  },
  previewEmailContent: {
    margin: "0 30px 30px 30px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #000000",
    borderRadius: "16px",
    padding: "34px 20px",
    maxHeight: "1111px",
    overflow: "auto",
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 400,
    fontSize: "18px",
    lineHeight: "200%",
    color: "#000000",
    "& *": {
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 400,
      fontSize: "18px",
      lineHeight: "200%",
      color: "#000000",
    },
  },
  // Reusable utility classes
  boldText: {
    fontWeight: 700,
  },
  goldLink: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    textDecoration: "underline",
    cursor: "pointer",
  },
  centerFlexContainer: {
    display: "flex",
    justifyContent: "center",
  },
  marginTop35: {
    marginTop: "35px",
  },
  centerWithPaddingBottom: {
    textAlign: "center" as const,
    paddingBottom: "40px",
  },
  createButton: {
    backgroundColor: theme.palette.primary.main,
    fontSize: "24px",
    fontWeight: "bold",
    borderRadius: "12px",
    padding: "15px 96px",
    textTransform: "none" as const,
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
      padding: "14px 32px",
      width: "100%",
      maxWidth: "320px",
    },
  },
  loadingSpinner: {
    color: "white",
  },
  loadingSpinnerGold: {
    color: theme.palette.primary.main,
  },
  closeIconSmall: {
    fontSize: "20px",
  },
  dialogContentCentered: {
    padding: "0 30px 30px 30px",
    textAlign: "center" as const,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "80px 0",
  },
  marginTop24: {
    marginTop: "24px",
  },
}));
// export const ShowInAppSwitch = styled((props: SwitchProps) => (
//   <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
// ))(({ theme }) => ({
//   width: 42,
//   height: 26,
//   margin: "10px",
//   padding: 0,
//   "& .MuiSwitch-switchBase": {
//     padding: 0,
//     margin: 2,

//     transitionDuration: "300ms",
//     "&.Mui-checked": {
//       transform: "translateX(16px)",
//       color: "#fff",
//       "& + .MuiSwitch-track": {
//         opacity: 1,
//         border: 0,
//       },
//       "&.Mui-disabled + .MuiSwitch-track": {
//         opacity: 0.5,
//       },
//     },
//     "&.Mui-focusVisible .MuiSwitch-thumb": {
//       color: "#33cf4d",
//       border: "6px solid #fff",
//     },
//   },
//   "& .MuiSwitch-thumb": {
//     boxSizing: "border-box",
//     width: 22,
//     height: 22,
//   },
//   "& .MuiSwitch-track": {
//     borderRadius: 26 / 2,
//     opacity: 1,
//     transition: theme.transitions.create(["background-color"], {
//       duration: 500,
//     }),
//   },
//   "& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
//     backgroundColor: theme.palette.primary.main,
//     border: "1px solid black",
//   },
//   "& .MuiSwitch-root": {
//     margin: "10px",
//   },
// }));

const convertToBase64 = (file: any) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

const CreateChamberMember = (): ReactElement => {
  const classes = useStyles();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const enumStrings = useEnumStrings();

  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [customError, setCustomError] = useState(
    enumStrings.shopLocal.updateChamberShopLocalCampaign.anErrorOccurred
  );
  const [invalidError, setInvalidError] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [name, setname] = useState("");
  const [open, setOpen] = useState(false);
  const [createChamberMember] = useCreateChamberMembersMutation();
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const [categoryDisable, setCategoryDisable] = useState(false);
  // Track whether user has directly interacted with admin fields (stops auto-sync for that field)
  const adminFieldsTouched = React.useRef({
    adminConsoleContactName: false,
    cpaAdminCompanyAdminContactTitle: false,
    adminConsoleLoginEmail: false,
    cpaAdminCompanyAdminContactNumber: false,
    cpaAdminCompanyAdminContactNumberExtension: false,
  });
  const primaryContactNameTouched = React.useRef(false);
  const [isSameAsCompanyContact] = useState(false);
  const [contactChamberDialogOpen, setContactChamberDialogOpen] = useState(false);

  // Success modal state and hooks
  const [successModalLoading, setSuccessModalLoading] = useState(false);
  const [successNewPassword, setSuccessNewPassword] = useState("");
  const [sampleEmailModalOpen, setSampleEmailModalOpen] = useState(false);
  const [downloadConfirmationOpen, setDownloadConfirmationOpen] = useState(false);
  const [emailConfirmationOpen, setEmailConfirmationOpen] = useState(false);

  const [setNewTemporaryChamberMemberPassword] =
    useSetNewTemporaryChamberMemberPasswordMutation();
  const [sendLoginInfoEmailToChamberMember] =
    useSendLoginInfoEmailToChamberMemberMutation();

  const initialValues: any = {
    id: "",
    chamberId: "",
    categoryId: [],
    name: "",
    description: "",
    website: "",
    facebook: "",
    instagram: "",
    threads: "",
    linkedin: "",
    twitter: "",
    tiktok: "",
    youtube: "",
    googleReviewLink: "",
    address: {
      city: "",
      postalCode: "",
      province: "",
      streetAddress: "",
    },
    phoneNumber: "",
    phoneNumberExtension: "",
    contactName: "",
    primaryContactName: "",
    primaryContactTitle: "",
    primaryContactEmail: "",
    primaryContactPhoneNumber: "",
    primaryContactPhoneNumberExtension: "",
    email: "",
    logo: "",
    showInApp: true,
    hideAddressFromDirectory: false,
    adminConsoleLoginEmail: "",
    adminConsoleContactName: "",
    cpaAdminCompanyAdminContactTitle: "",
    cpaAdminCompanyAdminContactNumber: "",
    cpaAdminCompanyAdminContactNumberExtension: "",
    cpaEmployeeAdoptionContactName: "",
    cpaEmployeeAdoptionContactTitle: "",
    cpaEmployeeAdoptionContactEmail: "",
    cpaEmployeeAdoptionContactNumber: "",
    cpaEmployeeAdoptionContactNumberExtension: "",
    customContacts: getInitialStateOfCustomContacts(null),
  };

  const redirectToManageChamber = useRedirect(
    RedirectUri.manageAMemberChamberAdmin
  );

  const handleClose = useCallback(async (): Promise<void> => {
    setOpen(false);
    redirectToManageChamber();
  }, [redirectToManageChamber]);

  const [
    suggestedAlternativeEmailUsernameModalOpen,
    setSuggestedAlternativeEmailUsernameModalOpen,
  ] = useState(false);

  // Email already in use dialog state
  const [emailInUseDialogOpen, setEmailInUseDialogOpen] = useState(false);

  const closeSuggestedAlternativeEmailUsernameModal = useCallback(() => {
    setSuggestedAlternativeEmailUsernameModalOpen(false);
  }, []);

  const openSuggestedAlternativeEmailUsernameModal = useCallback(() => {
    setSuggestedAlternativeEmailUsernameModalOpen(true);
  }, []);

  const [suggestedPlusAddressedEmail, setSuggestedPlusAddressedEmail] =
    useState("");

  const [newChamberMemberId, setNewChamberMemberId] = useState("");

  // IDA AI Generate Description state
  const [idaDialogOpen, setIdaDialogOpen] = useState(false);
  const [idaKeyDetails, setIdaKeyDetails] = useState("");
  const [idaGeneratedText, setIdaGeneratedText] = useState("");
  const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [idaSuccessDialogOpen, setIdaSuccessDialogOpen] = useState(false);
  const [idaGeneratedDescription, setIdaGeneratedDescription] = useState(false);

  // Sample email query for preview
  const { data: getSampleLoginEmail } = useGetSampleLoginInfoEmailQuery({
    variables: {
      type: LoginInfoEmailTypes.BrandNew,
      chamberMemberId: newChamberMemberId || "",
    },
    skip: !newChamberMemberId,
  });

  // CSV data for download
  const csvData = useMemo(
    () => [
      [
        enumStrings.sendLoginModal.memberEmail,
        enumStrings.sendLoginModal.memberPassword,
      ],
      [username, successNewPassword],
    ],
    [
      enumStrings.sendLoginModal.memberEmail,
      enumStrings.sendLoginModal.memberPassword,
      username,
      successNewPassword,
    ]
  );

  // Download credentials handler
  const executeDownloadCredentials = useCallback(async () => {
    setSuccessModalLoading(true);
    const { data } = await setNewTemporaryChamberMemberPassword({
      variables: {
        adminConsoleLoginEmail: username || "",
        chamberMemberId: newChamberMemberId || "",
      },
    });
    setSuccessNewPassword(
      data?.setNewTemporaryChamberMemberPassword.password || ""
    );
    setSuccessModalLoading(false);
    setDownloadConfirmationOpen(true);
    // Trigger CSV download
    setTimeout(() => {
      const button = document.getElementById("success-modal-csv-download");
      button?.click();
    }, 100);
  }, [username, newChamberMemberId, setNewTemporaryChamberMemberPassword]);

  // Send email handler
  const executeSendCredentialsEmail = useCallback(async () => {
    setSuccessModalLoading(true);
    const { data } = await sendLoginInfoEmailToChamberMember({
      variables: {
        adminConsoleLoginEmail: username || "",
        chamberMemberId: newChamberMemberId || "",
        type: LoginInfoEmailTypes.BrandNew,
      },
      refetchQueries: ["getAllChamberMembers"],
      awaitRefetchQueries: true,
    });
    if (
      data?.sendLoginInfoEmailToChamberMember?.loginInfoEmailSentSuccessfully
    ) {
      setEmailConfirmationOpen(true);
      setTimeout(() => setEmailConfirmationOpen(false), 5000);
    }
    setSuccessModalLoading(false);
  }, [username, newChamberMemberId, sendLoginInfoEmailToChamberMember]);

  // Auto-close email sent confirmation after 15 seconds
  useEffect(() => {
    if (emailConfirmationOpen) {
      const timer = setTimeout(() => {
        setEmailConfirmationOpen(false);
        handleClose();
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [emailConfirmationOpen, handleClose]);

  const id = uuidv4();

  const validationSchema = yup.object({
    name: yup
      .string()
      .required(enumStrings.validationSchema.companyNameRequired),
    description: yup.string(),
    contactName: yup
      .string(),
    phoneNumber: yup
      .string(),
    email: yup.string().email(enumStrings.validationSchema.notValidEmail),
    primaryContactName: yup
      .string()
      .required(enumStrings.validationSchema.primaryContactNameRequired || "Primary Contact Name is required"),
    primaryContactEmail: yup
      .string()
      .email(enumStrings.validationSchema.notValidEmail)
      .required(enumStrings.validationSchema.contactEmailRequired || "Primary Contact Email is required"),
    adminConsoleLoginEmail: yup
      .string()
      .email(enumStrings.validationSchema.notValidEmail)
      .required(enumStrings.validationSchema.adminConsoleLoginEmailRequired || "Primary Contact Email is required"),
    primaryContactPhoneNumber: yup
      .string(),
    primaryContactTitle: yup
      .string(),
    adminConsoleContactName: yup
      .string()
      .required(enumStrings.validationSchema.dashboardContactNameRequired),
    cpaAdminCompanyAdminContactNumber: yup
      .string(),
    address: addressValidationSchema,
    categoryId: yup
      .array()
      .min(1, enumStrings.validationSchema.categoryRequired),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: any) => {
      setInvalidError(false);
      setCustomError(``);

      if (!chamberInfo || !chamberInfo.id) {
        setInvalidError(true);
        setCustomError(
          `${enumStrings.perkPartnerAdmin.unableToCreateMemberPleaseTryAgin}`
        );
        return;
      }

      setLoader(true);
      try {
        setButtonDisabled(true);
        const chamberMemberUser = await createChamberMember({
          variables: {
            input: {
              data: [
                {
                  address: {
                    city: input.address.city || "",
                    postalCode: input.address.postalCode || "",
                    province: input.address.province || "",
                    streetAddress: input.address.streetAddress || "",
                  },
                  categoryId: input.categoryId,
                  chamberId: chamberInfo.id,
                  description: (input.description || "").trim(),
                  contactName: (input.contactName || "").trim(),
                  email: (input.email || "").trim().toLowerCase(),
                  phoneNumber: input.phoneNumber,
                  phoneNumberExtension: input.phoneNumberExtension || "",
                  showInApp: input.showInApp,
                  hideAddressFromDirectory: input.hideAddressFromDirectory ?? false,
                  name: input.name.trim(),
                  website: input.website,
                  facebook: input.facebook,
                  instagram: input.instagram,
                  threads: input.threads,
                  linkedin: input.linkedin,
                  twitter: input.twitter,
                  tiktok: input.tiktok,
                  youtube: input.youtube,
                  googleReviewLink: input.googleReviewLink,
                  logo: input.logo,
                  id: id,
                  customContacts: input.customContacts,
                  // adding ignore here cuz adminConsoleLoginEmail will ALWAYS be given on creation
                  // @ts-ignore
                  adminConsoleLoginEmail: input.adminConsoleLoginEmail
                    .trim()
                    .toLowerCase(),
                  primaryContactName: (input.primaryContactName || "").trim(),
                  primaryContactTitle: (input.primaryContactTitle || "").trim(),
                  primaryContactEmail: (input.primaryContactEmail || input.adminConsoleLoginEmail || "").trim().toLowerCase(),
                  primaryContactPhoneNumber: input.primaryContactPhoneNumber || "",
                  primaryContactPhoneNumberExtension: input.primaryContactPhoneNumberExtension || "",
                  adminConsoleContactName: (
                    input.adminConsoleContactName || ""
                  ).trim(),
                  cpaAdminCompanyAdminContactTitle: (input.cpaAdminCompanyAdminContactTitle || "").trim(),
                  cpaAdminCompanyAdminContactNumber: input.cpaAdminCompanyAdminContactNumber || "",
                  cpaAdminCompanyAdminContactNumberExtension: input.cpaAdminCompanyAdminContactNumberExtension || "",
                  cpaEmployeeAdoptionContactName: (input.cpaEmployeeAdoptionContactName || "").trim(),
                  cpaEmployeeAdoptionContactTitle: (input.cpaEmployeeAdoptionContactTitle || "").trim(),
                  cpaEmployeeAdoptionContactEmail: (input.cpaEmployeeAdoptionContactEmail || "").trim().toLowerCase(),
                  cpaEmployeeAdoptionContactNumber: input.cpaEmployeeAdoptionContactNumber || "",
                  cpaEmployeeAdoptionContactNumberExtension: input.cpaEmployeeAdoptionContactNumberExtension || "",
                },
              ],
            },
          },
        });
        const creationResult =
          chamberMemberUser.data?.createChamberMembers.results[0];
        setNewChamberMemberId(id);
        if (creationResult?.chamberMemberAddedSuccessfully === false) {
          setInvalidError(true);
          setCustomError(
            `${enumStrings.perkPartnerAdmin.unableToCreateMember} ${(creationResult.failureReason || "").includes(
              "UsernameExistsException"
            )
              ? enumStrings.perkPartnerAdmin.alreadyExists
              : creationResult.failureReason || ""
            }`
          );
          setLoader(false);
          setOpen(false);
          setButtonDisabled(false);

          if (
            creationResult.failureReason?.includes("UsernameExistsException")
          ) {
            setSuggestedPlusAddressedEmail(
              (creationResult.failureReason || "").trim().split(" ")[1]
            );
            openSuggestedAlternativeEmailUsernameModal();
          }

          return;
        }
        const usernameVal = creationResult?.email as string;
        const nameVal = input.name;

        setUsername(usernameVal);
        setname(nameVal);

        setOpen(true);
        setLoader(false);
        // Clear the dirty flag so the UnsavedChangesPrompt doesn't trigger after a successful save.
        formik.resetForm({ values: formik.values });

        console.log(formik.errors);
      } catch (error) {
        console.log(formik.errors);
        setOpen(false);
        setInvalidError(true);
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  // Scroll to first validation error on submit
  useEffect(() => {
    scrollToFirstValidationError(formik.submitCount, formik.errors);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount, formik.errors]);

  const handleUseSuggestedPlusAddressedAlternative = useCallback(() => {
    formik.setFieldValue(
      "adminConsoleLoginEmail",
      suggestedPlusAddressedEmail.trim().toLowerCase()
    );
    formik.submitForm();
    closeSuggestedAlternativeEmailUsernameModal();
  }, [
    suggestedPlusAddressedEmail,
    closeSuggestedAlternativeEmailUsernameModal,
    formik,
  ]);

  /* eslint-disable react-hooks/exhaustive-deps */
  // Auto-populate admin contact fields from primary contact in real-time
  // Stops syncing a field once user clicks into (focuses) that admin field directly
  React.useEffect(() => {
    if (!adminFieldsTouched.current.adminConsoleContactName) {
      formik.setFieldValue("adminConsoleContactName", formik.values.primaryContactName);
    }
  }, [formik.values.primaryContactName]);

  React.useEffect(() => {
    if (!adminFieldsTouched.current.cpaAdminCompanyAdminContactTitle) {
      formik.setFieldValue("cpaAdminCompanyAdminContactTitle", formik.values.primaryContactTitle);
    }
  }, [formik.values.primaryContactTitle]);

  React.useEffect(() => {
    if (!adminFieldsTouched.current.adminConsoleLoginEmail) {
      formik.setFieldValue("adminConsoleLoginEmail", formik.values.primaryContactEmail);
    }
  }, [formik.values.primaryContactEmail]);

  React.useEffect(() => {
    if (!adminFieldsTouched.current.cpaAdminCompanyAdminContactNumber) {
      formik.setFieldValue("cpaAdminCompanyAdminContactNumber", formik.values.primaryContactPhoneNumber);
    }
  }, [formik.values.primaryContactPhoneNumber]);

  React.useEffect(() => {
    if (!adminFieldsTouched.current.cpaAdminCompanyAdminContactNumberExtension) {
      formik.setFieldValue("cpaAdminCompanyAdminContactNumberExtension", formik.values.primaryContactPhoneNumberExtension);
    }
  }, [formik.values.primaryContactPhoneNumberExtension]);

  React.useEffect(() => {
    if (isSameAsCompanyContact) {
      formik.setFieldValue("cpaEmployeeAdoptionContactName", formik.values.adminConsoleContactName);
      formik.setFieldValue("cpaEmployeeAdoptionContactTitle", formik.values.cpaAdminCompanyAdminContactTitle);
      formik.setFieldValue("cpaEmployeeAdoptionContactEmail", formik.values.adminConsoleLoginEmail);
      formik.setFieldValue("cpaEmployeeAdoptionContactNumber", formik.values.cpaAdminCompanyAdminContactNumber);
      formik.setFieldValue("cpaEmployeeAdoptionContactNumberExtension", formik.values.cpaAdminCompanyAdminContactNumberExtension);
    }
  }, [
    isSameAsCompanyContact,
    formik.values.adminConsoleContactName,
    formik.values.cpaAdminCompanyAdminContactTitle,
    formik.values.adminConsoleLoginEmail,
    formik.values.cpaAdminCompanyAdminContactNumber,
    formik.values.cpaAdminCompanyAdminContactNumberExtension,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleCategoriesChange = (event: { target: { value: any } }) => {
    const value = event.target.value;
    formik.setFieldTouched("categoryId", true, false);
    formik.setFieldValue("categoryId", value);
    if (value.length >= 2) {
      setCategoryDisable(true);
    } else {
      setCategoryDisable(false);
    }
  };

  const handleDelete = (value: string) => {
    const newArray = _without(formik.values.categoryId, value);
    formik.setFieldTouched("categoryId", true, false);
    formik.setFieldValue("categoryId", newArray);
    if (newArray.length < 2) {
      setCategoryDisable(false);
    }
  };

  // const handleToggleChange = useCallback(
  //   (e) => {
  //     console.log(e.target.checked);
  //     formik.setFieldValue("showInApp", e.target.checked);
  //   },
  //   [formik]
  // );

  const handleCreateBase64 = useCallback(
    async (e) => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      const base64 = await convertToBase64(file);
      formik.setFieldValue("logo", base64);
    },
    [formik]
  );
  const deleteLogo = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    formik.setFieldValue("logo", undefined);
  };

  // Check if email is already in use (mock check for demo - shows dialog for specific email)
  const checkEmailInUse = useCallback((email: string) => {
    const testEmail = "thomasjames@balanceathletics.com";
    if (email.toLowerCase().trim() === testEmail.toLowerCase()) {
      setEmailInUseValue(email);
      setEmailInUseDialogOpen(true);
      return true;
    }
    return false;
  }, []);

  const handleEmailBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value;
    if (email && email.includes("@")) {
      checkEmailInUse(email);
    }
  }, [checkEmailInUse]);

  // @ts-ignore
  const rootAdminObj =
    // @ts-ignore
    enumStrings?.groups[UserType.ChamberAdmin][
    RedirectUri.createAMemberChamberAdmin
    ];
  return (
    <div className={classes.root}>
      <UnsavedChangesPrompt isDirty={formik.dirty} />
      <div className={classes.titleContent}>
        <div className={classes.titleRow}>
          <div
            className={classes.title}
            style={{
              flexDirection: "column",
            }}
          >
            <span>{rootAdminObj.title}</span>
            <span
              style={{
                marginLeft: "1px",
                fontWeight: "normal",
                fontSize: isMobile ? "16px" : "18px",
                lineHeight: isMobile ? "1.55" : undefined,
                opacity: 0.9,
              }}
            >
              {rootAdminObj.description}
            </span>
            <div style={{ height: 6 }} />
            <div
              className={classes.pageHeaderButtonRow}
              style={{ display: "flex", gap: isMobile ? "8px" : "12px", alignItems: isMobile ? "stretch" : "center", flexWrap: "wrap", flexDirection: isMobile ? "column" : "row" }}
            >
              <VideoTutorialButton
                tutorialUrl="https://share.synthesia.io/embeds/videos/e9ae72e1-71f3-44fb-a5bc-d71d35cab428"
              />
              <ExportButton
                customOnClick={() => openInstructionPdf("Create New Member.pdf")}
                icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
                label={enumStrings.manageChamberEngage.viewInstructions}
                disablePadding
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.content}>
        <div>
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            {/* Company Information Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.companyInformation}</p>
              </div>
              <p className={classes.sectionHelperText}>
                {enumStrings.createMemberForm.companyInfoDesc}
              </p>
              <div className={classes.companyInfoGrid}>
                {/* Company Name - Left Row 1 */}
                <div style={{ gridArea: "companyName" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.companyName}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.abcPlumbing}
                    className={classes.companyInput}
                    variant="outlined"
                    id="name"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>

                {/* Company Description - Right Row 1+2 (spans two rows) */}
                <div style={{ gridArea: "description" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.companyDescription}</span>
                  </div>
                  <div className={classes.descriptionHelperText} dangerouslySetInnerHTML={{ __html: "Enter a company description to showcase your products and services, or click <b>Generate</b> to have <b>IDA (AI)</b> create one for you automatically." }} />

                  <div style={{
                    border: (formik.touched.description || formik.submitCount > 0) && formik.errors.description
                      ? "1px solid #f44336"
                      : idaGeneratedDescription
                        ? `2px solid ${theme.palette.success.main}`
                        : `1px solid ${theme.palette.text.primary}`,
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                  }}>
                    {idaGeneratedDescription && (
                      <div style={{
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 700,
                        fontSize: "10px",
                        lineHeight: "15px",
                        color: theme.palette.success.main,
                        marginBottom: "4px",
                      }}>
                        {enumStrings.idaGenerate.generatedLabel}
                      </div>
                    )}
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm.placeholders.enterCompanyDescription}
                      className={classes.companyInput}
                      variant="standard"
                      multiline
                      rows={3}
                      inputProps={{
                        maxLength: CHARACTER_LIMIT_DESCRIPTION,
                      }}
                      id="description"
                      name="description"
                      value={formik.values.description}
                      onChange={(e) => {
                        formik.handleChange(e);
                        if (idaGeneratedDescription) {
                          setIdaGeneratedDescription(false);
                        }
                      }}
                      error={
                        (formik.touched.description || formik.submitCount > 0) &&
                        Boolean(formik.errors.description)
                      }
                      helperText={
                        (formik.touched.description || formik.submitCount > 0) &&
                        formik.errors.description
                      }
                      InputProps={{
                        disableUnderline: true,
                      }}
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ flex: 1 }}
                      fullWidth
                    />
                  </div>
                  {/* IDA AI Helper Section - below the textarea: counter left, icon + generate right */}
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: "12px", marginTop: "12px", flexWrap: "wrap" }}>
                    <div style={{ fontSize: "14px", color: theme.palette.text.secondary, textAlign: "left", fontFamily: "Inter, sans-serif" }}>
                      {`${formik.values?.description?.length || 0}/${CHARACTER_LIMIT_DESCRIPTION} ${enumStrings.createMemberForm.characters}`}
                    </div>
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "12px" }}>
                      <img src={IdaChatbotIcon} alt="IDA" style={{ width: "40px", height: "40px", objectFit: "contain" }} />
                      {chamberInfo?.isSandbox ? (
                        <Button
                          variant="contained"
                          onClick={() => {
                            setIdaDialogOpen(true);
                          }}
                          style={{
                            backgroundColor: "#4CAF50",
                            color: "#FFFFFF",
                            fontSize: "14px",
                            fontWeight: 700,
                            borderRadius: "8px",
                            padding: "8px 24px",
                            textTransform: "uppercase",
                            minHeight: "36px",
                            boxShadow: "none",
                          }}
                        >
                          {idaGeneratedDescription ? enumStrings.idaGenerate.idaRegenerate : enumStrings.idaGenerate.generateButton}
                        </Button>
                      ) : (
                        <span style={{
                          backgroundColor: "#E53935",
                          color: "#FFFFFF",
                          border: "1px solid #E53935",
                          borderRadius: "10px",
                          padding: "6px 14px",
                          fontSize: "12px",
                          fontWeight: 700,
                          whiteSpace: "nowrap" as const,
                          display: "inline-block",
                        }}>
                          COMING SOON
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Company Website - Left Row 2 */}
                <div style={{ gridArea: "companyWebsite" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.companyWebsite}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterCompanyWebsite}
                    className={classes.companyInput}
                    variant="outlined"
                    id="website"
                    name="website"
                    value={formik.values.website}
                    onChange={formik.handleChange}
                    error={formik.touched.website && Boolean(formik.errors.website)}
                    helperText={formik.touched.website && formik.errors.website}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>

                {/* Main Company Contact Name - Left Row 3 */}
                <div style={{ gridArea: "contactName" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.companyContactName}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.mainContactNamePlaceholder}
                    className={classes.companyInput}
                    variant="outlined"
                    id="contactName"
                    name="contactName"
                    value={formik.values.contactName}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.contactName &&
                      Boolean(formik.errors.contactName)
                    }
                    helperText={
                      formik.touched.contactName && formik.errors.contactName
                    }
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>

                {/* Company Contact Phone - Right Row 3 */}
                <div style={{ gridArea: "phone" }}>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className={classes.fieldLabel} style={{ flex: 1 }}>
                      <span>{enumStrings.createMemberForm.companyContactPhone}</span>
                    </div>
                    <div className={classes.fieldLabel} style={{ width: "100px" }}>
                      <span>{enumStrings.createMemberForm?.extensionNo}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm.placeholders.enterCompanyContactPhone}
                      className={classes.companyInput}
                      variant="outlined"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formik.values.phoneNumber}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.phoneNumber &&
                        Boolean(formik.errors.phoneNumber)
                      }
                      helperText={
                        formik.touched.phoneNumber && formik.errors.phoneNumber
                      }
                      InputLabelProps={{ shrink: false }}
                      InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ flex: 1 }}
                    />
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm?.extensionPlaceholder || "00000"}
                      className={classes.companyInput}
                      variant="outlined"
                      id="phoneNumberExtension"
                      name="phoneNumberExtension"
                      value={formik.values.phoneNumberExtension}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ width: "100px" }}
                    />
                  </div>
                </div>

                {/* Main Company Contact Email - Left Row 4 */}
                <div style={{ gridArea: "contactEmail" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.companyContactEmail}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterCompanyContactEmail}
                    className={classes.companyInput}
                    variant="outlined"
                    id="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={handleEmailBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>

                {/* Google Review URL - Right Row 4 */}
                <div style={{ gridArea: "googleReview" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.googleReviewUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterGoogleReviewUrl}
                    className={classes.companyInput}
                    variant="outlined"
                    id="googleReviewLink"
                    name="googleReviewLink"
                    value={formik.values.googleReviewLink as any}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>

                {/* Company Logo - Left Row 5 */}
                <div style={{ gridArea: "logo" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.logo}</span>
                  </div>
                  <div className={classes.logoHelperText}>
                    {enumStrings.createMemberForm.logoHelperText}
                  </div>
                  <LogoPicker
                    logo={formik.values.logo}
                    deleteLogo={deleteLogo}
                    base64={(e: any) => handleCreateBase64(e)}
                  />
                </div>

                {/* Chamber Perks App Categories - Right Row 5 */}
                <div style={{ gridArea: "categories" }}>
                  <div className={classes.fieldLabel}>
                    <span style={{ display: "flex", alignItems: "flex-start", gap: "4px" }}>
                      {enumStrings.createMemberForm.chamberPerksCategories} <span className={classes.requiredAsterisk}>*</span>
                      <img src={IdaChatbotIcon} alt="IDA" style={{ width: "73px", height: "57px", objectFit: "contain", marginTop: "-15px" }} />
                    </span>
                  </div>
                  <div className={classes.categoryHelperText}>
                    Our AI, "<span style={{ fontWeight: 700 }}>IDA</span>" has selected the two most relevant categories for you based on your company description. You're welcome to adjust these by removing or selecting different categories from the dropdown.
                  </div>
                  <Categories
                    selectedCategories={formik.values.categoryId}
                    handleCategoriesChange={handleCategoriesChange}
                    disableCategories={categoryDisable}
                    handleDelete={handleDelete}
                    error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
                  />
                  {formik.touched.categoryId && formik.errors.categoryId && (
                  <FormHelperText
                    component="legend"
                    style={{
                      color: "#f44336",
                      paddingLeft: "0",
                      paddingTop: "5px",
                      textAlign: "left",
                    }}
                  >
                    {formik.errors.categoryId}
                  </FormHelperText>
                  )}
                  <p style={{ fontSize: "16px", color: "#24272C", marginTop: "16px", marginBottom: "0" }} dangerouslySetInnerHTML={{ __html: enumStrings.createMemberForm.categoryDescriptionHelper }} />
                </div>
              </div>
            </div>

            {/* Company Address Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.companyAddressTitle}</p>
              </div>
              <p className={classes.sectionHelperText}>
                {enumStrings.createMemberForm.companyAddressDesc}
              </p>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.hideAddressFromDirectory}
                    onChange={formik.handleChange}
                    name="hideAddressFromDirectory"
                    color="default"
                    style={{ color: "#000" }}
                  />
                }
                label={
                  <span style={{ fontSize: "14px", color: "#000" }}>
                    {enumStrings.createMemberForm?.hideAddressCheckbox}
                  </span>
                }
                style={{ marginBottom: "8px", marginTop: "-12px", marginLeft: "0px" }}
              />

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.streetAddress}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterStreetAddress}
                    className={classes.companyInput}
                    variant="outlined"
                    id="address.streetAddress"
                    name="address.streetAddress"
                    value={formik.values.address.streetAddress}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.city}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterCity}
                    className={classes.companyInput}
                    variant="outlined"
                    id="address.city"
                    name="address.city"
                    value={formik.values.address.city}
                    onChange={formik.handleChange}
                    error={
                      (formik.touched.address as any)?.city &&
                      Boolean((formik.errors.address as any)?.city)
                    }
                    helperText={
                      (formik.touched.address as any)?.city &&
                      (formik.errors.address as any)?.city
                    }
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.provinceState}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <ProvincePicker
                    formikVal={formik.values.address?.province}
                    formikErrors={
                      (formik.touched.address as any)?.province &&
                      Boolean((formik.errors.address as any)?.province)
                    }
                    formikStringError={(formik.errors.address as any)?.province}
                    formikSetFieldValue={formik.setFieldValue}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.postalZipCode}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterPostalCode}
                    className={classes.companyInput}
                    variant="outlined"
                    id="address.postalCode"
                    name="address.postalCode"
                    value={formik.values.address.postalCode}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>
            </div>

            {/* Links to Socials Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.linksToSocials}</p>
              </div>
              <p className={classes.sectionHelperText}>
                {enumStrings.createMemberForm.socialsDesc}
              </p>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.facebookUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterFacebookUrl}
                    className={classes.companyInput}
                    variant="outlined"
                    id="facebook"
                    name="facebook"
                    value={formik.values.facebook}
                    onChange={formik.handleChange}
                    error={formik.touched.facebook && Boolean(formik.errors.facebook)}
                    helperText={formik.touched.facebook && formik.errors.facebook}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.instagramUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterInstagramUrl}
                    className={classes.companyInput}
                    variant="outlined"
                    id="instagram"
                    name="instagram"
                    value={formik.values.instagram}
                    onChange={formik.handleChange}
                    error={formik.touched.instagram && Boolean(formik.errors.instagram)}
                    helperText={formik.touched.instagram && formik.errors.instagram}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.threadsUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterThreadsUrl}
                    className={classes.companyInput}
                    variant="outlined"
                    id="threads"
                    name="threads"
                    value={formik.values.threads}
                    onChange={formik.handleChange}
                    error={formik.touched.threads && Boolean(formik.errors.threads)}
                    helperText={formik.touched.threads && formik.errors.threads}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.linkedinUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterLinkedinUrl}
                    className={classes.companyInput}
                    variant="outlined"
                    id="linkedin"
                    name="linkedin"
                    value={formik.values.linkedin}
                    onChange={formik.handleChange}
                    error={formik.touched.linkedin && Boolean(formik.errors.linkedin)}
                    helperText={formik.touched.linkedin && formik.errors.linkedin}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.xTwitterUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterXTwitterUrl}
                    className={classes.companyInput}
                    variant="outlined"
                    id="twitter"
                    name="twitter"
                    value={formik.values.twitter}
                    onChange={formik.handleChange}
                    error={formik.touched.twitter && Boolean(formik.errors.twitter)}
                    helperText={formik.touched.twitter && formik.errors.twitter}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.tiktokUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterTiktokUrl}
                    className={classes.companyInput}
                    variant="outlined"
                    id="tiktok"
                    name="tiktok"
                    value={formik.values.tiktok}
                    onChange={formik.handleChange}
                    error={formik.touched.tiktok && Boolean(formik.errors.tiktok)}
                    helperText={formik.touched.tiktok && formik.errors.tiktok}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.youtubeUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterYoutubeUrl}
                    className={classes.companyInput}
                    variant="outlined"
                    id="youtube"
                    name="youtube"
                    value={formik.values.youtube}
                    onChange={formik.handleChange}
                    error={formik.touched.youtube && Boolean(formik.errors.youtube)}
                    helperText={formik.touched.youtube && formik.errors.youtube}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div />
              </div>
            </div>

            {/* Company Primary Contact Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.companyPrimaryContact}</p>
              </div>
              <p className={classes.sectionHelperText}>
                Enter the Primary Contact name and details below. This contact information will <strong>NOT</strong> appear in the app unless it is also listed as the Main Company contact.
              </p>
              <p className={classes.sectionHelperText}>
                The Chamber Perks App™ <strong>member dashboard credentials (including the email username and temporary password)</strong> will be created and sent to the Primary Contact, who can use them to update the company's business directory and create or manage perks, or to <strong>assign a Company Administrator below to receive the credentials and manage the Chamber Perks App™ on their behalf.</strong>
              </p>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.primaryContactName}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterPrimaryContactName}
                    className={classes.companyInput}
                    variant="outlined"
                    id="primaryContactName"
                    name="primaryContactName"
                    value={formik.values.primaryContactName}
                    onFocus={() => { primaryContactNameTouched.current = true; }}
                    onChange={formik.handleChange}
                    error={formik.touched.primaryContactName && Boolean(formik.errors.primaryContactName)}
                    helperText={formik.touched.primaryContactName && formik.errors.primaryContactName}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.title}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterContactTitle}
                    className={classes.companyInput}
                    variant="outlined"
                    id="primaryContactTitle"
                    name="primaryContactTitle"
                    value={formik.values.primaryContactTitle}
                    onChange={formik.handleChange}
                    error={formik.touched.primaryContactTitle && Boolean(formik.errors.primaryContactTitle)}
                    helperText={formik.touched.primaryContactTitle && formik.errors.primaryContactTitle}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow} style={{ alignItems: "end" }}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.primaryContactEmail}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <div style={{ fontSize: "16px", color: "#000000", marginBottom: "8px" }}>
                    {enumStrings.createMemberForm.emailUsernameNote}
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterPrimaryContactEmail}
                    className={classes.companyInput}
                    variant="outlined"
                    id="primaryContactEmail"
                    name="primaryContactEmail"
                    value={formik.values.primaryContactEmail}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.primaryContactEmail &&
                      Boolean(formik.errors.primaryContactEmail)
                    }
                    helperText={
                      formik.touched.primaryContactEmail &&
                      formik.errors.primaryContactEmail
                    }
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className={classes.fieldLabel} style={{ flex: 1 }}>
                      <span>{enumStrings.createMemberForm.primaryContactPhone}</span>
                    </div>
                    <div className={classes.fieldLabel} style={{ width: "100px" }}>
                      <span>{enumStrings.createMemberForm?.extensionNo}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm.placeholders.enterPrimaryContactPhone}
                      className={classes.companyInput}
                      variant="outlined"
                      id="primaryContactPhoneNumber"
                      name="primaryContactPhoneNumber"
                      value={formik.values.primaryContactPhoneNumber}
                      onChange={formik.handleChange}
                      error={formik.touched.primaryContactPhoneNumber && Boolean(formik.errors.primaryContactPhoneNumber)}
                      helperText={formik.touched.primaryContactPhoneNumber && formik.errors.primaryContactPhoneNumber}
                      InputLabelProps={{ shrink: false }}
                      InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ flex: 1 }}
                    />
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm?.extensionPlaceholder || "00000"}
                      className={classes.companyInput}
                      variant="outlined"
                      id="primaryContactPhoneNumberExtension"
                      name="primaryContactPhoneNumberExtension"
                      value={formik.values.primaryContactPhoneNumberExtension}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ width: "100px" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Chamber Perks App Company Administrator Contact Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.chamberPerksAdminContact}</p>
              </div>
              <p className={classes.sectionHelperText}>
                {enumStrings.createMemberForm.adminContactAutoAssignDesc}
              </p>
              <p className={classes.sectionHelperText} style={{ fontWeight: 700 }}>
                {enumStrings.createMemberForm.adminContactNotAppear}
              </p>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.contactName}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterContactName}
                    className={classes.companyInput}
                    variant="outlined"
                    id="adminConsoleContactName"
                    name="adminConsoleContactName"
                    value={formik.values.adminConsoleContactName}
                    onFocus={() => { adminFieldsTouched.current.adminConsoleContactName = true; }}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.adminConsoleContactName &&
                      Boolean(formik.errors.adminConsoleContactName)
                    }
                    helperText={
                      formik.touched.adminConsoleContactName &&
                      formik.errors.adminConsoleContactName
                    }
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.title}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterContactTitle}
                    className={classes.companyInput}
                    variant="outlined"
                    id="cpaAdminCompanyAdminContactTitle"
                    name="cpaAdminCompanyAdminContactTitle"
                    value={formik.values.cpaAdminCompanyAdminContactTitle}
                    onFocus={() => { adminFieldsTouched.current.cpaAdminCompanyAdminContactTitle = true; }}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow} style={{ alignItems: "end" }}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.contactEmail}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#666666", marginBottom: "8px" }}>
                    {enumStrings.createMemberForm.adminEmailNote}
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterContactEmail}
                    className={classes.companyInput}
                    variant="outlined"
                    id="adminConsoleLoginEmail"
                    name="adminConsoleLoginEmail"
                    value={formik.values.adminConsoleLoginEmail}
                    onFocus={() => { adminFieldsTouched.current.adminConsoleLoginEmail = true; }}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.adminConsoleLoginEmail &&
                      Boolean(formik.errors.adminConsoleLoginEmail)
                    }
                    helperText={
                      formik.touched.adminConsoleLoginEmail &&
                      formik.errors.adminConsoleLoginEmail
                    }
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className={classes.fieldLabel} style={{ flex: 1 }}>
                      <span>{enumStrings.createMemberForm.contactPhone}</span>
                    </div>
                    <div className={classes.fieldLabel} style={{ width: "100px" }}>
                      <span>{enumStrings.createMemberForm?.extensionNo}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm.placeholders.enterContactPhone}
                      className={classes.companyInput}
                      variant="outlined"
                      id="cpaAdminCompanyAdminContactNumber"
                      name="cpaAdminCompanyAdminContactNumber"
                      value={formik.values.cpaAdminCompanyAdminContactNumber}
                      onFocus={() => { adminFieldsTouched.current.cpaAdminCompanyAdminContactNumber = true; }}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.cpaAdminCompanyAdminContactNumber &&
                        Boolean(formik.errors.cpaAdminCompanyAdminContactNumber)
                      }
                      helperText={
                        formik.touched.cpaAdminCompanyAdminContactNumber &&
                        formik.errors.cpaAdminCompanyAdminContactNumber
                      }
                      InputLabelProps={{ shrink: false }}
                      InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ flex: 1 }}
                    />
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm?.extensionPlaceholder || "00000"}
                      className={classes.companyInput}
                      variant="outlined"
                      id="cpaAdminCompanyAdminContactNumberExtension"
                      name="cpaAdminCompanyAdminContactNumberExtension"
                      value={formik.values.cpaAdminCompanyAdminContactNumberExtension}
                      onFocus={() => { adminFieldsTouched.current.cpaAdminCompanyAdminContactNumberExtension = true; }}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ width: "100px" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact for Employee Adoption Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.employeeAdoptionContact}</p>
              </div>
              <p className={classes.sectionHelperText}>
                {enumStrings.createMemberForm.employeeAdoptionDesc}
              </p>
              <p className={classes.sectionHelperText} style={{ fontWeight: 700 }}>
                {enumStrings.createMemberForm.employeeAdoptionNotAppear}
              </p>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.employeeContactName}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterEmployeeContactName}
                    className={classes.companyInput}
                    variant="outlined"
                    id="cpaEmployeeAdoptionContactName"
                    name="cpaEmployeeAdoptionContactName"
                    value={formik.values.cpaEmployeeAdoptionContactName}
                    onChange={formik.handleChange}
                    disabled={isSameAsCompanyContact}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.title}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterContactTitle}
                    className={classes.companyInput}
                    variant="outlined"
                    id="cpaEmployeeAdoptionContactTitle"
                    name="cpaEmployeeAdoptionContactTitle"
                    value={formik.values.cpaEmployeeAdoptionContactTitle}
                    onChange={formik.handleChange}
                    disabled={isSameAsCompanyContact}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.employeeContactEmail}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterEmployeeContactEmail}
                    className={classes.companyInput}
                    variant="outlined"
                    id="cpaEmployeeAdoptionContactEmail"
                    name="cpaEmployeeAdoptionContactEmail"
                    value={formik.values.cpaEmployeeAdoptionContactEmail}
                    onChange={formik.handleChange}
                    disabled={isSameAsCompanyContact}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className={classes.fieldLabel} style={{ flex: 1 }}>
                      <span>{enumStrings.createMemberForm.employeeContactPhone}</span>
                    </div>
                    <div className={classes.fieldLabel} style={{ width: "100px" }}>
                      <span>{enumStrings.createMemberForm?.extensionNo}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm.placeholders.enterEmployeeContactPhone}
                      className={classes.companyInput}
                      variant="outlined"
                      id="cpaEmployeeAdoptionContactNumber"
                      name="cpaEmployeeAdoptionContactNumber"
                      value={formik.values.cpaEmployeeAdoptionContactNumber}
                      onChange={formik.handleChange}
                      disabled={isSameAsCompanyContact}
                      InputLabelProps={{ shrink: false }}
                      InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ flex: 1 }}
                    />
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm?.extensionPlaceholder || "00000"}
                      className={classes.companyInput}
                      variant="outlined"
                      id="cpaEmployeeAdoptionContactNumberExtension"
                      name="cpaEmployeeAdoptionContactNumberExtension"
                      value={formik.values.cpaEmployeeAdoptionContactNumberExtension}
                      onChange={formik.handleChange}
                      disabled={isSameAsCompanyContact}
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ width: "100px" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Featured Member Profile Banners Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeaderBlack}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.featuredMemberBannersTitle}</p>
              </div>
              <p className={classes.bannerDescText}>
                {enumStrings.createMemberForm.featuredMemberBannersDesc}
              </p>
              <p className={classes.bannerNotUpgradedText}>
                {enumStrings.editMemberForm.featuredMemberNotActivatedDesc}
              </p>
              <FeaturedMemberBannerGuidance className={classes.bannerAspectRatioText} />
              <div className={classes.bannerGrid}>
                {[1, 2, 3, 4].map((num) => (
                  <div key={num}>
                    <p className={classes.bannerSlotLabel}>{enumStrings.createMemberForm.bannerLabel} {num}</p>
                    <div className={classes.bannerSlot}>
                      <img src={UploadRoundedIcon} alt="Upload" className={classes.bannerSlotIcon} />
                      <span className={classes.bannerSlotText}>{enumStrings.createMemberForm.uploadBannerLabel}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className={classes.centerFlexContainer}>
                <Button
                  className={classes.previewBannersButton}
                  disabled
                >
                  {enumStrings.createMemberForm.previewBannersButton}
                </Button>
              </div>
            </div>

            {/* <MultiContactForm
              formikValuesCustomContacts={formik.values.customContacts}
              formikSetFieldValue={formik.setFieldValue}
            />
            <CSVLink
              data={csvData}
              filename={`${name} Member Dashboard Credentials.csv`}
              id="csv-download-button"
              style={{ display: "none" }}
            /> */}
            <div className={classes.marginTop35}>
              {invalidError ? (
                <div className={classes.invalidError}>{customError}</div>
              ) : null}
            </div>
            {formik.submitCount > 0 && Object.keys(formik?.errors || {}).length > 0 ? (
              <div>
                <div className={classes.invalidError}>
                  {enumStrings.ChamberMemberSelector.pleaseAddressAnyIssues}
                </div>
                <br />
              </div>
            ) : null}
            <div className={classes.centerWithPaddingBottom}>
              <Button
                type="submit"
                size="large"
                color="primary"
                variant="contained"
                className={classes.createButton}
                disabled={loader || buttonDisabled}
              >
                {loader ? (
                  <CircularProgress className={classes.loadingSpinner} />
                ) : (
                  enumStrings.createMemberForm.createMemberButton
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
      {suggestedAlternativeEmailUsernameModalOpen && (
        <CreateChamberMemberSuggestedAlternativePlusAddressedEmailUsernameModal
          open={suggestedAlternativeEmailUsernameModalOpen}
          handleClose={closeSuggestedAlternativeEmailUsernameModal}
          handleUseSuggestedPlusAddressedAlternative={
            handleUseSuggestedPlusAddressedAlternative
          }
          currentAdminConsoleLoginEmail={
            formik.values.adminConsoleLoginEmail || ""
          }
          suggestedPlusAddressedEmail={suggestedPlusAddressedEmail}
        />
      )}
      {/* New Member Created Successfully Dialog */}
      <Dialog
        open={open && !downloadConfirmationOpen && !emailConfirmationOpen && !sampleEmailModalOpen}
        classes={{ paper: classes.successDialogPaper }}
      >
        <IconButton
          className={classes.successCloseButton}
          onClick={handleClose}
        >
          <CloseIcon className={classes.closeIconSmall} />
        </IconButton>
        <DialogContent className={classes.dialogContentCentered}>
          {successModalLoading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress className={classes.loadingSpinnerGold} />
            </div>
          ) : (
            <>
              <div className={classes.successIconCircle}>
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="30" cy="30" r="25" stroke="#FFFFFF" strokeWidth="4" fill="none" />
                  <path d="M18 30L26 38L42 22" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </div>
              <p className={classes.successTitle}>
                {enumStrings.createMemberForm.successModalTitle}
              </p>
              {formik.values.isSuspended ? (
                <>
                  <p className={classes.successDesc}>
                    {enumStrings.sendLoginModal.description}
                  </p>
                  <div className={classes.marginTop24}>
                    <Button
                      className={classes.successDownloadButton}
                      onClick={handleClose}
                      variant="contained"
                      style={{ maxWidth: "300px" }}
                    >
                      {enumStrings.sendLoginModal.close}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className={classes.successDesc}>
                    {enumStrings.createMemberForm.successModalDesc}
                  </p>
                  <div className={classes.successStackedContainer}>
                    {/* Manual Section */}
                    <div>
                      <p className={classes.successSectionTitle}>
                        {enumStrings.createMemberForm.successModalManualTitle}
                      </p>
                      <p className={classes.successSectionDesc}>
                        <span dangerouslySetInnerHTML={{ __html: enumStrings.createMemberForm.successModalManualDesc.replace(
                          "{memberCompanyName}",
                          name || ""
                        ) }} />
                      </p>
                      <div className={classes.successButtonsRow}>
                        <Button
                          className={classes.successDownloadButton}
                          onClick={executeDownloadCredentials}
                          variant="contained"
                        >
                          {enumStrings.createMemberForm.successModalDownloadCredentials}
                        </Button>
                        <Button
                          className={classes.successPreviewEmailButton}
                          onClick={() => setSampleEmailModalOpen(true)}
                          variant="contained"
                        >
                          {enumStrings.createMemberForm.successModalPreviewEmail}
                        </Button>
                      </div>
                    </div>
                    {/* Automatic Section */}
                    <div>
                      <p className={classes.successSectionTitle}>
                        {enumStrings.createMemberForm.successModalAutoTitle}
                      </p>
                      <p className={classes.successSectionDesc}>
                        <span dangerouslySetInnerHTML={{ __html: enumStrings.createMemberForm.successModalAutoDesc
                          .replace("{memberCompanyName}", name || "")
                          .split("{memberEmail}")[0] }} />
                        <span style={{ fontWeight: 700 }}>
                          {removePlusAddress({
                            adminConsoleLoginEmail: username || "",
                            chamberName: chamberInfo?.name || "",
                          })}
                        </span>
                        <span dangerouslySetInnerHTML={{ __html: enumStrings.createMemberForm.successModalAutoDesc
                          .replace("{memberCompanyName}", name || "")
                          .split("{memberEmail}")[1] || "" }} />
                      </p>
                      <div className={classes.successButtonsRow}>
                        <Button
                          className={classes.successSendEmailButton}
                          onClick={executeSendCredentialsEmail}
                          variant="contained"
                        >
                          {enumStrings.createMemberForm.successModalSendEmail}
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <CSVLink
        data={csvData}
        filename={`${name} member dashboard credentials.csv`}
        id="success-modal-csv-download"
        style={{ display: "none" }}
      />

      {/* Credentials Downloaded Dialog */}
      <Dialog
        open={downloadConfirmationOpen}
        onClose={() => {
          setDownloadConfirmationOpen(false);
          handleClose();
        }}
        classes={{ paper: classes.credentialsDialogPaper }}
      >
        <IconButton
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            zIndex: 1,
            backgroundColor: theme.palette.error.main,
            color: "#fff",
            width: "28px",
            height: "28px",
          }}
          onClick={() => {
            setDownloadConfirmationOpen(false);
            handleClose();
          }}
        >
          <CloseIcon style={{ fontSize: "18px" }} />
        </IconButton>
        <DialogContent style={{ padding: "10px 24px 30px 24px", textAlign: "center" }}>
          <div className={classes.credentialsIconCircle}>
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="30" cy="30" r="25" stroke="#FFFFFF" strokeWidth="4" fill="none" />
              <path d="M18 30L26 38L42 22" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </div>
          <p className={classes.credentialsTitle}>
            {enumStrings.createMemberForm.credentialsDownloadedTitle}
          </p>
          <p className={classes.credentialsDesc}>
            {enumStrings.createMemberForm.credentialsDownloadedDesc}
          </p>
          <div style={{ marginTop: "16px" }}>
            <CSVLink
              data={csvData}
              filename={`${name} member dashboard credentials.csv`}
              style={{ textDecoration: "none" }}
            >
              <Button
                className={classes.credentialsDownloadBtn}
                variant="contained"
              >
                {enumStrings.createMemberForm.credentialsDownloadedButton}
              </Button>
            </CSVLink>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Sent Successfully Dialog */}
      <Dialog
        open={emailConfirmationOpen}
        onClose={() => setEmailConfirmationOpen(false)}
        classes={{ paper: classes.emailSentDialogPaper }}
      >
        <IconButton
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            zIndex: 1,
            backgroundColor: theme.palette.error.main,
            color: "#fff",
            width: "28px",
            height: "28px",
          }}
          onClick={() => setEmailConfirmationOpen(false)}
        >
          <CloseIcon style={{ fontSize: "18px" }} />
        </IconButton>
        <DialogContent style={{ padding: "0 24px 30px 24px", textAlign: "center" }}>
          <div className={classes.emailSentIconCircle}>
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="30" cy="30" r="25" stroke="#FFFFFF" strokeWidth="4" fill="none" />
              <path d="M18 30L26 38L42 22" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </div>
          <p className={classes.emailSentTitle}>
            {enumStrings.createMemberForm.emailSentTitle}
          </p>
        </DialogContent>
      </Dialog>

      {/* Preview Email Dialog */}
      <Dialog
        open={sampleEmailModalOpen}
        onClose={() => setSampleEmailModalOpen(false)}
        classes={{ paper: classes.previewEmailDialogPaper }}
        maxWidth={false}
      >
        <div className={classes.previewEmailHeader}>
          <IconButton
            className={classes.previewEmailBackButton}
            onClick={() => setSampleEmailModalOpen(false)}
          >
            <ArrowBackIcon style={{ fontSize: "28px", color: "#000000" }} />
          </IconButton>
          <span className={classes.previewEmailTitle}>
            {enumStrings.createMemberForm.previewEmailTitle}
          </span>
          <div style={{ width: "38px" }} />
        </div>
        <div className={classes.previewEmailContent}>
          <style>{`.email-preview-no-bold, .email-preview-no-bold * { font-family: DM Sans, sans-serif !important; font-weight: 400 !important; font-size: 18px !important; line-height: 200% !important; color: #000000 !important; } .email-preview-no-bold a { text-decoration: underline !important; }`}</style>
          <div
            className="email-preview-no-bold"
            dangerouslySetInnerHTML={{
              __html: getSampleLoginEmail?.getSampleLoginInfoEmail?.emailHtml || "",
            }}
          />
        </div>
      </Dialog>

      {/* Email Already In Use Dialog */}
      <Dialog
        open={emailInUseDialogOpen}
        onClose={() => setEmailInUseDialogOpen(false)}
        classes={{ paper: classes.emailDialogPaper }}
      >
        <IconButton
          className={classes.dialogCloseButton}
          onClick={() => setEmailInUseDialogOpen(false)}
        >
          <CloseIcon style={{ fontSize: "20px" }} />
        </IconButton>
        <DialogContent style={{ padding: "0 34px 40px 34px", textAlign: "center" }}>
          <div className={classes.dialogIconContainer}>
            <div className={classes.dialogIconError}>
              <ErrorOutlineIcon style={{ fontSize: "60px", color: "#FFFFFF" }} />
            </div>
          </div>
          <div className={classes.dialogTitle}>{enumStrings.createMemberForm.emailAlreadyInUse}</div>
          <div className={classes.dialogDescription}>
            {enumStrings.createMemberForm.emailAlreadyInUseDesc}
          </div>
          <div className={classes.dialogDescription}>
            {enumStrings.createMemberForm.emailAlreadyInUseDesc2}
          </div>
          <div className={classes.dialogDescription}>
            Click <span style={{ fontWeight: 700 }}>Use Alternative</span> to continue and create member credentials using the alternative + email.
          </div>
          <div className={classes.dialogButtons}>
            <Button
              className={classes.dialogButtonOk}
              onClick={() => {
                setEmailInUseDialogOpen(false);
                formik.setFieldValue("email", "");
              }}
            >
              {enumStrings.createMemberForm.useAlternative}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Chamber Dialog */}
      <ActionDialog
        open={contactChamberDialogOpen}
        onClose={() => setContactChamberDialogOpen(false)}
        icon={<img src={MdiEmailFastOutlineIcon} alt="Email" style={{ width: "40px", height: "40px" }} />}
        iconBgColor="#4CAF50"
        title={enumStrings.createMemberForm.contactChamberTitle}
        description={enumStrings.createMemberForm.contactChamberDesc}
        buttonText={enumStrings.createMemberForm.contactChamberSendRequest}
        onButtonClick={() => {
          const memberName = formik.values.name || "";
          const chamberName = chamberInfo?.name || "";
          const toEmail = chamberInfo?.supportContactEmail || "";
          const subject = encodeURIComponent(
              enumStrings.createMemberForm.contactChamberEmailSubject.replace("{memberName}", memberName)
          );
          const body = encodeURIComponent(
              enumStrings.createMemberForm.contactChamberEmailBody
                  .replace("{chamberName}", chamberName)
                  .replace("{memberName}", memberName)
          );
          window.open(`mailto:${toEmail}?subject=${subject}&body=${body}`, "_blank");
          setContactChamberDialogOpen(false);
        }}
      />

      {/* IDA Generate Description Dialog */}
      <IDAGenerateModal
        open={idaDialogOpen}
        onClose={() => setIdaDialogOpen(false)}
        keyDetails={idaKeyDetails}
        onKeyDetailsChange={setIdaKeyDetails}
        entityName={formik.values.name || "the company"}
        entityType="company"
        onGenerate={(generated) => {
          setIdaGeneratedText(generated);
          setIdaDialogOpen(false);
          setOverwriteDialogOpen(true);
        }}
      />

      {/* IDA Overwrite Confirmation Dialog */}
      <IDAOverwriteDialog
        open={overwriteDialogOpen}
        onClose={() => setOverwriteDialogOpen(false)}
        onRegenerate={() => {
          setOverwriteDialogOpen(false);
          setIdaDialogOpen(true);
        }}
        onConfirm={() => {
          formik.setFieldValue("description", idaGeneratedText);
          setOverwriteDialogOpen(false);
          setIdaGeneratedDescription(true);
          setIdaSuccessDialogOpen(true);
          setTimeout(() => setIdaSuccessDialogOpen(false), 5000);
        }}
        title={enumStrings.idaGenerate.overwriteTitle}
        description={enumStrings.idaGenerate.overwriteDesc}
        generatedLabel={enumStrings.idaGenerate.generatedLabel}
        generatedText={idaGeneratedText}
      />

      {/* IDA Success Dialog */}
      <SuccessDialog
        open={idaSuccessDialogOpen}
        onClose={() => setIdaSuccessDialogOpen(false)}
        message={enumStrings.toastMessages.idaGeneratedSuccess}
      />

      <ComingSoonDialog
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
      />
    </div>
  );
};

export default CreateChamberMember;
